import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/hooks/useCart";

export default function CartPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const {
    items,
    savedItems,
    itemCount,
    subtotal,
    removeItem,
    updateQuantity,
    clearCart,
    saveForLater,
    moveToCart,
    clearSaved,
  } = useCart();

  const shipping = subtotal >= 50 ? 0 : 5.99;
  const total = subtotal + shipping;

  // True empty — nothing in cart AND nothing saved
  if (items.length === 0 && savedItems.length === 0) {
    return (
      <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-12 flex-wrap">
                <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
                  {t("nav.home")}
                </Link>
                <span>/</span>
                <span className="text-foreground-900 font-medium">{t("cart.title")}</span>
              </div>

              <div className="max-w-lg mx-auto text-center py-12">
                <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-background-100 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-4xl text-foreground-300"></i>
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {t("cart.empty")}
                </h2>
                <p className="text-foreground-500 mb-8 leading-relaxed text-sm md:text-base">
                  {t("cart.empty_message")}
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-shopping-bag-line"></i>
                  {t("cart.start_shopping")}
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Shared cart item row component for desktop ──────────
  const CartItemRow = ({ item, isSaved }: { item: typeof items[0]; isSaved: boolean }) => {
    const itemTotal = item.price * item.quantity;
    const variantText = Object.entries(item.selectedVariants)
      .filter(([, v]) => v)
      .map(([, v]) => v)
      .join(" / ");

    return (
      <div className="px-4 sm:px-6 py-5 border-b border-background-200 last:border-0 hover:bg-background-100/50 transition-colors">
        {/* Mobile */}
        <div className="sm:hidden">
          <div className="flex gap-4">
            <Link
              to={`/product/${item.slug}`}
              className="w-24 h-24 rounded-lg overflow-hidden bg-background-100 flex-shrink-0 cursor-pointer"
            >
              <img
                src={`${item.imageUrl}&width=200&height=200`}
                alt={isAr ? item.nameAr : item.nameEn}
                className="w-full h-full object-cover"
              />
            </Link>
            <div className="flex-1 min-w-0">
              <Link
                to={`/product/${item.slug}`}
                className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors line-clamp-2 cursor-pointer"
              >
                {isAr ? item.nameAr : item.nameEn}
              </Link>
              {variantText && (
                <p className="text-xs text-foreground-500 mt-1">{variantText}</p>
              )}
              <p className="text-sm font-semibold text-foreground-950 mt-1.5">
                ${item.price.toFixed(2)}
              </p>
              <div className="flex items-center justify-between mt-3">
                {isSaved ? (
                  <button
                    onClick={() => moveToCart(item.cartKey)}
                    className="px-3 py-2 text-xs font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-line mr-1"></i>
                    {t("cart.move_to_cart")}
                  </button>
                ) : (
                  <div className="flex items-center border border-background-200 rounded-lg">
                    <button
                      onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                      className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer rounded-l-lg"
                    >
                      <i className="ri-subtract-line text-xs"></i>
                    </button>
                    <span className="w-9 h-8 flex items-center justify-center text-sm font-medium text-foreground-900 border-x border-background-200">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer rounded-r-lg"
                    >
                      <i className="ri-add-line text-xs"></i>
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {!isSaved && (
                    <button
                      onClick={() => saveForLater(item.cartKey)}
                      className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bookmark-line mr-0.5"></i>
                      Save
                    </button>
                  )}
                  <button
                    onClick={() => (isSaved ? moveToCart(item.cartKey) : removeItem(item.cartKey))}
                    className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className={isSaved ? "ri-shopping-cart-line" : "ri-delete-bin-line"}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop row */}
        <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
          <div className="col-span-5 flex items-center gap-4">
            <Link
              to={`/product/${item.slug}`}
              className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 flex-shrink-0 cursor-pointer"
            >
              <img
                src={`${item.imageUrl}&width=128&height=128`}
                alt={isAr ? item.nameAr : item.nameEn}
                className="w-full h-full object-cover"
              />
            </Link>
            <div className="min-w-0">
              <Link
                to={`/product/${item.slug}`}
                className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors line-clamp-2 cursor-pointer"
              >
                {isAr ? item.nameAr : item.nameEn}
              </Link>
              {variantText && (
                <p className="text-xs text-foreground-500 mt-0.5">{variantText}</p>
              )}
            </div>
          </div>

          <div className="col-span-2 text-center">
            <span className="text-sm font-medium text-foreground-900">
              ${item.price.toFixed(2)}
            </span>
          </div>

          <div className="col-span-2 flex justify-center">
            {isSaved ? (
              <span className="text-xs text-foreground-400 italic">—</span>
            ) : (
              <div className="flex items-center border border-background-200 rounded-lg">
                <button
                  onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                  className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer rounded-l-lg"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="w-9 h-8 flex items-center justify-center text-sm font-medium text-foreground-900 border-x border-background-200">
                  {item.quantity}
                </span>
                <button
                  onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                  className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer rounded-r-lg"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
            )}
          </div>

          <div className="col-span-2 text-right">
            <span className="text-sm font-semibold text-foreground-950">
              {isSaved ? "—" : `$${itemTotal.toFixed(2)}`}
            </span>
          </div>

          <div className="col-span-1 flex justify-center gap-1">
            {isSaved ? (
              <button
                onClick={() => moveToCart(item.cartKey)}
                className="px-2.5 py-1.5 text-xs font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                title={t("cart.move_to_cart")}
              >
                <i className="ri-shopping-cart-line"></i>
              </button>
            ) : (
              <>
                <button
                  onClick={() => saveForLater(item.cartKey)}
                  className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                  title={t("cart.save_for_later")}
                >
                  <i className="ri-bookmark-line"></i>
                </button>
                <button
                  onClick={() => removeItem(item.cartKey)}
                  className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  title={t("cart.remove")}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
              <div>
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-1 flex-wrap">
                  <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
                    {t("nav.home")}
                  </Link>
                  <span>/</span>
                  <span className="text-foreground-900 font-medium">{t("cart.title")}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {t("cart.title")}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {itemCount === 1 ? t("cart.item_count", { count: itemCount }) : t("cart.item_count_plural", { count: itemCount })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {items.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200 rounded-lg hover:bg-background-100 hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line mr-1.5"></i>
                    Clear Cart
                  </button>
                )}
                <Link
                  to="/products"
                  className="px-4 py-2.5 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("cart.continue_shopping")}
                </Link>
              </div>
            </div>

            <div className={`grid grid-cols-1 ${items.length > 0 ? "lg:grid-cols-3" : ""} gap-8 ${isAr ? "lg:direction-rtl" : ""}`}>
              {/* Cart Items */}
              <div className={items.length > 0 ? "lg:col-span-2" : ""}>
                {items.length > 0 && (
                  <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                    <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-4 bg-background-100 border-b border-background-200 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      <div className="col-span-5">Product</div>
                      <div className="col-span-2 text-center">Price</div>
                      <div className="col-span-2 text-center">Quantity</div>
                      <div className="col-span-2 text-right">Total</div>
                      <div className="col-span-1"></div>
                    </div>

                    {items.map((item) => (
                      <CartItemRow key={item.cartKey} item={item} isSaved={false} />
                    ))}
                  </div>
                )}

                {/* Saved for Later */}
                {savedItems.length > 0 && (
                  <div className={`${items.length > 0 ? "mt-8" : ""}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                          <i className="ri-bookmark-line text-accent-600 text-lg"></i>
                        </div>
                        <div>
                          <h2 className="text-lg font-heading font-bold text-foreground-950">
                            {t("cart.saved_title")}
                          </h2>
                          <p className="text-sm text-foreground-500">
                            {savedItems.length === 1
                              ? t("cart.saved_count", { count: savedItems.length })
                              : t("cart.saved_count_plural", { count: savedItems.length })}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={clearSaved}
                        className="px-3 py-2 text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        {t("cart.clear_saved")}
                      </button>
                    </div>

                    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden opacity-90">
                      <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-4 bg-background-100 border-b border-background-200 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                        <div className="col-span-5">Product</div>
                        <div className="col-span-2 text-center">Price</div>
                        <div className="col-span-2 text-center">Quantity</div>
                        <div className="col-span-2 text-right">Total</div>
                        <div className="col-span-1"></div>
                      </div>

                      {savedItems.map((item) => (
                        <CartItemRow key={item.cartKey} item={item} isSaved={true} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary - only visible when there are cart items */}
              {items.length > 0 && (
                <div className="lg:col-span-1">
                  <div className="sticky top-24 bg-background-50 border border-background-200 rounded-2xl p-6">
                    <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                      {t("cart.summary")}
                    </h3>

                    <div className="flex gap-2 mb-6">
                      <input
                        type="text"
                        placeholder={t("cart.coupon_placeholder")}
                        className="flex-1 px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                      <button className="px-4 py-2.5 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                        {t("cart.apply")}
                      </button>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-600">{t("cart.subtotal")}</span>
                        <span className="font-medium text-foreground-900">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-600">{t("cart.shipping")}</span>
                        <span className={`font-medium ${shipping === 0 ? "text-green-600" : "text-foreground-900"}`}>
                          {shipping === 0 ? t("cart.free_shipping") : `$${shipping.toFixed(2)}`}
                        </span>
                      </div>
                      {shipping > 0 && (
                        <p className="text-xs text-foreground-400">
                          Free shipping on orders over $50 — you&apos;re ${(50 - subtotal).toFixed(2)} away!
                        </p>
                      )}
                    </div>

                    <div className="border-t border-background-200 mt-4 pt-4">
                      <div className="flex items-center justify-between mb-6">
                        <span className="text-base font-semibold text-foreground-950">{t("cart.total")}</span>
                        <span className="text-xl font-bold text-foreground-950">${total.toFixed(2)}</span>
                      </div>

                      <Link
                        to="/checkout"
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        <i className="ri-lock-line"></i>
                        {t("cart.checkout")}
                      </Link>
                    </div>

                    <div className="mt-6 pt-6 border-t border-background-200 space-y-2.5">
                      <div className="flex items-center gap-2.5 text-xs text-foreground-500">
                        <i className="ri-shield-check-line text-foreground-400"></i>
                        <span>Secure checkout with SSL encryption</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-xs text-foreground-500">
                        <i className="ri-truck-line text-foreground-400"></i>
                        <span>Free shipping on orders over $50</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-xs text-foreground-500">
                        <i className="ri-arrow-go-back-line text-foreground-400"></i>
                        <span>30-day easy returns</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}